import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface AIInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (instructions: string) => Promise<void>;
  isGenerating: boolean;
}

export function AIInstructionsDialog({ 
  open, 
  onOpenChange, 
  onGenerate, 
  isGenerating 
}: AIInstructionsDialogProps) {
  const { t, dir } = useLanguage();
  const [instructions, setInstructions] = useState('');

  const handleSubmit = async () => {
    if (!instructions.trim()) return;
    
    try {
      await onGenerate(instructions.trim());
      setInstructions(''); // Clear instructions after successful generation
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isGenerating) {
      onOpenChange(newOpen);
      if (!newOpen) {
        setInstructions(''); // Clear instructions when dialog closes
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" dir={dir}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            AI Question Generator
          </DialogTitle>
          <DialogDescription>
            Provide instructions for AI to generate custom questionnaire questions. 
            The AI will create questions with 2-6 options each, tailored for Saudi patients.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="instructions">Instructions for AI</Label>
            <Textarea
              id="instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Example: Create 5 questions about physical exercise habits, focusing on walking, gym activities, and sports. Include questions about frequency, duration, and barriers to exercise."
              className="min-h-[120px] resize-none"
              disabled={isGenerating}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">💡 Tips for better results:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Specify the number of questions you want</li>
              <li>• Mention specific topics or behaviors to focus on</li>
              <li>• Include context about the target audience (Saudi patients)</li>
              <li>• Specify if you want questions for specific categories</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-900 mb-2">⚠️ Note:</h4>
            <p className="text-sm text-amber-800">
              Generated questions will replace all existing questions in the questionnaire. 
              Make sure to save any important questions before generating new ones.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!instructions.trim() || isGenerating}
            className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Questions
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
